// Reader-facing copy for a credential-material mark.
//
// The tooltip on a flagged span is hover-only: no click, no scroll, about
// 300px at 11px. So it answers the reader's three questions in their order --
// what was found, how sure are we, was it reviewed -- and spends none of that
// room on the pipeline's own stage names.
//
// Two properties of the pipeline decide what is worth printing. Only promoted
// rows reach the dashboard, because the judge gate drops denies and
// abstentions into audit, so the verdict word is the same on nearly every mark
// and carries no information by itself. And the judge's own confidence gates
// nothing: it disagrees with its own verdict often enough that the promote
// floor is validated but no longer applied, so showing that number would ask
// for a trust the pipeline itself withholds. What survives is the detector,
// the classifier score, and whether review has happened yet.

// Detector tag -> what a person would call it. Mirrors `_DETECTORS` in
// backend/signals-catalog/credscan/signal.py. A tag missing here falls back to
// its own name with the underscores opened up, so a detector added on the
// backend is never anonymous while this map catches up.
export const DETECTOR_LABELS = {
    anthropic_api_key: "Anthropic API key",
    aws_access_key: "AWS access key ID",
    aws_secret_key: "AWS secret access key",
    azure_devops_pat: "Azure DevOps personal access token",
    basic_auth: "HTTP basic auth credentials",
    bearer_token: "Bearer token",
    connection_string: "Connection string with a password",
    env_secret: "Secret in an environment variable",
    gcp_gemini_api_key: "Google AI API key",
    gcp_service_account: "Google Cloud service account key",
    generic_secret: "Secret-looking value",
    github_token: "GitHub token",
    gitlab_token: "GitLab token",
    huggingface_token: "Hugging Face token",
    jwt: "JSON Web Token",
    mailgun_key: "Mailgun API key",
    npm_token: "npm token",
    openai_api_key: "OpenAI API key",
    pgp_private_key: "PGP private key",
    private_key: "Private key",
    putty_private_key: "PuTTY private key",
    pypi_token: "PyPI token",
    sendgrid_key: "SendGrid API key",
    short_password: "Password",
    slack_token: "Slack token",
    sshpass_password: "Password on an sshpass command",
    stripe_key: "Stripe API key",
    twilio_api_key: "Twilio API key",
    twitter_access_token: "X (Twitter) access token",
    twitter_api_key: "X (Twitter) API key",
    twitter_bearer_token: "X (Twitter) bearer token",
    url_credentials: "Credentials inside a URL",
};

// Band edges for the classifier score. The signal manifest drops anything
// below 0.20 at the classify stage, so every mark a reader can see is at least
// "Low" -- there is no band under this one to name.
const HIGH_FROM = 0.8;
const MEDIUM_FROM = 0.5;

export function detectorLabel(tag) {
    if (typeof tag !== "string" || !tag) return "Credential";
    return DETECTOR_LABELS[tag] || tag.replace(/_/g, " ");
}

// The score as a word. Returns null for anything that is not a usable score,
// so a caller can tell "unscored" from "scored low".
export function confidenceBand(score) {
    if (typeof score !== "number" || !Number.isFinite(score)) return null;
    if (score >= HIGH_FROM) return "High";
    if (score >= MEDIUM_FROM) return "Medium";
    return "Low";
}

// What review says, in the reader's terms rather than the rubric's. A verdict
// this function cannot read is treated as no verdict at all: the row is still
// a real promoted finding, and saying nothing about review beats asserting
// something about it from a shape we do not recognize.
function reviewSentence(judge) {
    if (!judge || typeof judge !== "object") return "Not reviewed.";
    if (judge.verdict === "deny") return "Reviewed and set aside.";
    if (judge.verdict !== "promote") return "Not reviewed.";
    return judge.cached === true ? "Reviewed and cached." : "Reviewed and kept.";
}

/**
 * The two lines of a credential-material tooltip.
 *
 * `title` always names the finding and, once it has been scored, how sure the
 * classifier is. `detail` is the quieter second line and is null when there is
 * nothing true to put there, which keeps a detector-only mark to one line
 * rather than padding it with an absence.
 */
export function credscanTipLines(entry) {
    const type = typeof entry?.type === "string" ? entry.type : "";
    const tag = type.includes(".") ? type.slice(type.indexOf(".") + 1) : type;
    const band = confidenceBand(entry?.confidence);
    const scored = band !== null;
    const judged = !!entry?.judge && typeof entry.judge === "object"
        && (entry.judge.verdict === "promote" || entry.judge.verdict === "deny");

    const title = scored
        ? `${detectorLabel(tag)} - ${band} confidence`
        : detectorLabel(tag);

    if (!scored && !judged) return { title, detail: null };
    const review = reviewSentence(judged ? entry.judge : null);
    const detail = scored
        ? `Model score ${Math.round(entry.confidence * 100)}%. ${review}`
        : review;
    return { title, detail };
}
